import { type Theme } from "@emotion/react";
import { Box, Stack, type SxProps } from "@mui/material";
import { Text, type TextProps } from "@src/appV2/redesign/system/ui-components";

import { getFormattedShiftPayBreakdown } from "../../Shift/getFormattedShiftPayBreakdown";
import { VisitPricingTypes, VisitTypes } from "../constants";
import { type VisitWithCaseData } from "../types";

interface VisitPayInfoLabelProps {
  visit: VisitWithCaseData;
  wrapperSx?: SxProps<Theme>;
  textVariant?: TextProps["variant"];
  totalPayTextVariant?: TextProps["variant"];
  showTotalPay?: boolean;
}

export function VisitPayInfoLabel(props: VisitPayInfoLabelProps) {
  const {
    visit,
    wrapperSx,
    textVariant = "h6",
    totalPayTextVariant = "body1",
    showTotalPay = true,
  } = props;
  const { payRate, estimatedDuration = 0, pricingType, type } = visit.attributes;

  if (!payRate?.amountInMinorUnits) {
    return null;
  }

  const payBreakdown = getFormattedShiftPayBreakdown(
    payRate.amountInMinorUnits / 100,
    (payRate.amountInMinorUnits * estimatedDuration) / 100,
    (payRate.amountInMinorUnits * estimatedDuration) / 100
  );

  const shouldShowTotalPay =
    showTotalPay && type === VisitTypes.REGULAR && pricingType === VisitPricingTypes.PER_HOUR;

  return (
    <Stack spacing={3} direction="row" alignItems="center" sx={wrapperSx}>
      {shouldShowTotalPay && (
        <Text
          variant={totalPayTextVariant}
          sx={(theme) => ({ color: theme.palette.text.tertiary })}
        >
          {payBreakdown?.totalPay}
        </Text>
      )}
      <Text semibold variant={textVariant}>
        {payBreakdown?.hourlyPay}{" "}
        <Box
          component="span"
          sx={(theme) => ({
            fontSize: theme.typography.caption.fontSize,
            lineHeight: theme.typography.caption.lineHeight,
          })}
        >
          {pricingType === VisitPricingTypes.PER_HOUR ? "/hr" : "/visit"}
        </Box>
      </Text>
    </Stack>
  );
}
